/* eslint-disable no-console */
import { Auth0Provider } from '@auth0/auth0-react';
import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { urlJoin } from 'url-join-ts';

import { PaymentPublicClient } from '../backend-client/payment-public-backend';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Loading from '../components/Loading';
import getEnv from '../utils/getEnv';
import { AuthContext } from './authContext';
import { getId, useAuth } from './AuthProvider';
import localAuth0CacheOptions from './localAuth0CacheOptions';

const {
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_PUBLIC_URL,
  REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
} = getEnv();

/**
 * Note: Auth0 needs explicit access to the history, so we need to get the history
 * with the useHistory hook. Here we're typing Auth0 react-router-dom together.
 *
 * Copied from:
 * https://auth0.com/blog/complete-guide-to-react-user-authentication/
 */
const Auth0ProviderWithHistory: React.FC = ({ children }) => {
  // Use the useLocation hook to access the current location
  const location = useLocation();
  const history = useHistory();
  const { isAuthenticated: isAuthAuthenticated, user } = useAuth();

  // The id in a /terms link (path or ?quote=) may be a legacy Quote id or an
  // ApplicationGroup id; getEmailAddress accepts either. This is only used to
  // pre-fill the Auth0 login hint, so no link canonicalization happens here —
  // the Terms page owns the /terms/<applicationGroupId> redirect.
  const inboundTncId =
    new URLSearchParams(location.search).get('quote') || getId('terms');

  const [emailAddress, setEmailAddress] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<unknown>();
  const [versionData, setVersionData] = useState<WorkflowVersion>();

  // Resolve the workflow version for the inbound ApplicationGroup id so the
  // email lookup below targets the version-matched payment-public backend
  // (mirrors the version-controller step used elsewhere). Non-fatal: if the id
  // can't be resolved (e.g. a legacy quote-id link), fall back to the
  // unversioned client so the login-hint prefill still works.
  useEffect(() => {
    if (!inboundTncId) {
      return;
    }

    let cancelled = false;
    new VersionControllerClient(REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL)
      .getVersionFromApplicationGroup(inboundTncId)
      .then((data) => {
        if (!cancelled) {
          setVersionData(data);
        }
      })
      .catch((err) => {
        console.error('Failed to resolve workflow version for tnc id', err);
      });

    return () => {
      cancelled = true;
    };
  }, [inboundTncId]);

  useEffect(() => {
    if (!inboundTncId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    new PaymentPublicClient(versionData?.paymentBackendVersion)
      .getEmailAddress(inboundTncId)
      .then((email) => {
        if (!cancelled) {
          setEmailAddress(email);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setEmailError(err);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inboundTncId, versionData]);

  const domain = REACT_APP_AUTH0_DOMAIN;
  const clientId = REACT_APP_AUTH0_CLIENT_ID;

  const origin = urlJoin(window.location.origin, REACT_APP_PUBLIC_URL) + '/';

  // Auth0Provider is the STABLE outer wrapper for both the loading window and
  // the loaded app. Previously the loading state returned a bare <div>, so the
  // provider was unmounted and remounted every time `isLoading` toggled. During
  // that window children such as ProtectedRoute -> withAuthenticationRequired
  // invoked Auth0 stub methods (e.g. loginWithRedirect) while no provider was
  // mounted, throwing "You forgot to wrap your component in <Auth0Provider>".
  // Keeping the provider mounted across the toggle prevents that. See NCP-4776.
  //
  // WHY THE `key`: @auth0/auth0-react v1 constructs its Auth0Client exactly
  // once, in a useState initializer on the provider's FIRST mount, and never
  // reacts to later prop changes. But `login_hint` (the customer email) is
  // fetched asynchronously and arrives AFTER that first mount, so a provider
  // mounted once would be frozen with `login_hint: undefined` — the Auth0 page
  // then shows the generic identifier screen with an empty email and never
  // sends the passwordless OTP. Keying on the email makes React recreate the
  // provider (and thus the client) exactly once, when `emailAddress` resolves
  // from undefined to a value, so the client is built WITH the hint. The swap
  // is a single atomic commit — children are never left without a provider, so
  // NCP-4776's "forgot to wrap your component" fix is preserved.
  const withAuth0Provider = (node: React.ReactNode) => (
    <Auth0Provider
      key={emailAddress ?? 'no-login-hint'}
      domain={domain}
      clientId={clientId}
      login_hint={emailAddress}
      redirectUri={origin}
      {...localAuth0CacheOptions}
      onRedirectCallback={(appState) => {
        const revisedReturnTo = appState?.returnTo
          ? appState?.returnTo.replace(`${REACT_APP_PUBLIC_URL}`, '')
          : window.location.pathname.replace(`${REACT_APP_PUBLIC_URL}`, '');
        history.replace(revisedReturnTo || window.location.pathname);
      }}
    >
      {node}
    </Auth0Provider>
  );

  if (isLoading) {
    return withAuth0Provider(
      <div className="grid grid-rows-main h-full w-full text-quartary">
        <Header usedWithRoutes={false} />
        <div
          className="flex"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading"
        >
          <Loading />
        </div>
        <Footer />
      </div>
    );
  }

  if (emailError) {
    throw Error('Failed when trying to get email address');
  }

  // Session-cookie auth path: Auth0 is intentionally not used here.
  if (isAuthAuthenticated) {
    return (
      <AuthContext.Provider value={{ login_hint: user?.email }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return withAuth0Provider(
    <AuthContext.Provider value={{ login_hint: emailAddress }}>
      {children}
    </AuthContext.Provider>
  );
};

export default Auth0ProviderWithHistory;
